import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import FAQSection from '@/components/page-specific/index/faq'
import { ContainerScroll } from '@/components/ui/container-scroll-animation'
import { Input } from '@/components/ui/input'
import { useSavedUrl } from '@/hooks/use-saved-url'
import { useAuth, useUser } from '@clerk/nextjs'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CircleDollarSign,
  CornerRightUp,
  Link2,
  MousePointerClick,
  Percent,
  Sparkles,
  ThumbsUp,
  Trophy,
  UserSearch,
  Volume2,
} from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

type FAQItem = {
  question: string
  answer: string
  linkText?: string
  linkHref?: string
}

const faqItems: FAQItem[] = [
  {
    question: 'What is the v0 Report Affiliate Program?',
    answer:
      'The v0 Report Affiliate Program allows you to earn commissions by promoting our AI document generation service. By sharing your unique referral link, you can earn money for every customer who signs up through your link.',
  },
  {
    question: 'How do I join the v0 Report Affiliate Program?',
    answer:
      'Joining is easy! Simply sign up through our affiliate portal and complete the application form. Once approved, you’ll receive your unique referral link and access to our marketing resources.',
  },
  {
    question: 'How do I promote v0 Report?',
    answer:
      'You can promote v0 Report through your website, blog, social media, email campaigns, and other channels. Use the marketing materials we provide to make your promotions more effective.',
  },
  {
    question: 'How much commission can I earn?',
    answer:
      'You can earn up to 30% commission on every sale made through your referral link. Additionally, enjoy recurring commissions as long as the referred customers remain subscribed.',
  },
  {
    question: 'How do I track my earnings?',
    answer:
      'Our affiliate dashboard allows you to track your clicks, conversions, and earnings in real-time. Log in to your account to monitor your performance and earnings.',
  },
  {
    question: 'When and how will I get paid?',
    answer:
      'Commissions are paid out monthly via your selected payment method. Ensure your payment details are up to date in your affiliate account to receive timely payments.',
  },
  {
    question: 'Can anyone join the v0 Report Affiliate Program?',
    answer:
      'Yes, anyone with an online presence can join. Whether you’re a blogger, influencer, content creator, or have a website dedicated to business solutions, you can start earning with v0 Report.',
  },
  {
    question: 'What support is available for affiliates?',
    answer:
      'We provide dedicated support to help you succeed. From promotional tips to answering your queries, our team is here to assist you every step of the way.',
  },
]

const inter = Inter({ subsets: ['latin'] })

const urlSchema = z.object({
  url: z.string(),
})

type UrlForm = z.infer<typeof urlSchema>

export default function Home() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  console.log(router.query)

  const { data: url } = useSavedUrl()

  const form = useForm<UrlForm>({
    resolver: zodResolver(urlSchema),
    defaultValues: {
      url: url || '',
    },
  })

  const { register, setValue, setFocus } = form

  React.useEffect(() => {
    setFocus('url')
  }, [setFocus])

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    router.push(isSignedIn ? '/pricing' : '/auth/signin')
  }

  const { user } = useUser()
  const isSubscribed = user?.publicMetadata.isSubscribed

  interface TrackingInfoProps {
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
    title: string
    description: string
  }

  const TrackingInfo: React.FC<TrackingInfoProps> = ({ Icon, title, description }) => {
    return (
      <div className="border p-8 rounded-3xl grid gap-4">
        <div className="bg-sky-100 p-2 flex items-center gap-2 w-fit rounded-full">
          <Icon className="w-9 h-9 text-sky-400 p-2 rounded-full" />
        </div>
        <div>
          <h3 className="text-lg font-medium mb-1">{title}</h3>
          <p className="text-zinc-500">{description}</p>
        </div>
      </div>
    )
  }

  const GettingStarted: React.FC<TrackingInfoProps> = ({ Icon, title, description }) => {
    return (
      <div className="border p-8 rounded-3xl grid gap-4">
        <Icon className="w-8 h-8 text-sky-400" />
        <div>
          <h3 className="text-lg font-medium mb-1">{title}</h3>
          <p className="text-zinc-500">{description}</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <NextSeo
        title="v0 report - Unleash the Power of Automated Report Generation"
        description="Discover v0 Report, your advanced AI Document Generator. In minutes, you can effortlessly create, customize, and manage professional-quality documents."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen  flex-col ${inter.className}`}>
          <div className="grid md:grid-cols-2 gap-8 mx-auto px-4 md:px-0 md:w-7/12 my-48 flex items-center">
            <div className="grid gap-4">
              <div>
                <h2 className="text-2xl text-zinc-700">
                  <span className="font-medium">Join v0 Report’s </span>
                  <span className="font-thin italic font-serif leading-none text-zinc-700">
                    Affiliate Program
                  </span>
                </h2>
                <p className="text-lg text-zinc-500 mt-4 grid gap-2">
                  <span>
                    We're thrilled to introduce you to the v0 Report Affiliate Program, designed for
                    individuals and businesses eager to promote an innovative AI document generation
                    service.
                  </span>
                  <span>
                    By joining our program, you can earn attractive commissions while helping us
                    revolutionize how reports are created.
                  </span>
                </p>
              </div>

              <Link
                href="https://affiliates.v0.report/signup"
                className="bg-sky-500 text-primary-foreground w-full md:w-fit rounded-lg px-4 py-2 mt-1 text-center"
              >
                <span>Apply now</span>
              </Link>
            </div>

            <img
              src="/images/stock/lady.png"
              className="ml-auto min-w-[150px] text-sm hidden sm:block rounded-lg"
              alt="thumbnail"
            />
          </div>

          <div className="mx-auto rounded-xl max-w-screen-lg px-4 md:px-0">
            <h2 className="w-fit px-4 mx-auto text-center text-3xl font-bold mb-10">
              Why Partner with v0 Report’s Affiliate Program
            </h2>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 mx-auto mb-24">
              <TrackingInfo
                Icon={Percent}
                title="High Commission Rates"
                description="Earn up to 30% commission on every sale you generate. Our competitive rates ensure
                  you are well-rewarded for your efforts."
              />
              <TrackingInfo
                Icon={Trophy}
                title="Recurring Revenue"
                description="Enjoy recurring commissions for the customer's lifetime, ensuring a steady income
                  stream as long as they remain subscribed."
              />
              <TrackingInfo
                Icon={ThumbsUp}
                title="Dedicated Support"
                description="We provide you with all the support you need, from tips on how to boost your conversions to answering any questions you might have."
              />
              <TrackingInfo
                Icon={CircleDollarSign}
                title="Real-Time Tracking"
                description="Stay updated with our real-time tracking system, which allows you to monitor your referrals and earnings effortlessly."
              />
            </div>
          </div>

          <div className="mx-auto mt-36">
            <h2 className="w-fit px-4 mx-auto text-center text-3xl font-bold mb-20">
              How v0 Report’s Affiliate Program Works
            </h2>
            <div className="grid grid-cols-1 gap-16 md:grid-cols-2 w-11/12 md:w-7/12 mx-auto mb-24">
              <div className="my-auto">
                <h3 className="text-3xl font-semibold mb-2">Sign up</h3>
                <p className="text-zinc-500">
                  Join our affiliate program by completing a simple application form.
                </p>
              </div>

              <img
                src="/images/graphic/signup.svg"
                className="ml-auto h-100 text-sm hidden sm:block"
                alt="thumbnail"
              />
            </div>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 w-11/12 md:w-7/12 mx-auto mb-24">
              <img
                src="/images/graphic/promote.svg"
                className="mr-auto h-100 text-sm hidden sm:block"
                alt="thumbnail"
              />
              <div className="my-auto">
                <h3 className="text-3xl font-semibold mb-2">Promote</h3>
                <p className="text-zinc-500">
                  Use your unique affiliate links to promote v0 Report across your website, blog,
                  social media, email lists, and other channels.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-16 md:grid-cols-2 w-11/12 md:w-7/12 mx-auto mb-24">
              <div className="my-auto">
                <h3 className="text-3xl font-semibold mb-2">🥳 Earn</h3>
                <p className="text-zinc-500">
                  Earn commissions for every customer who signs up through your link. It’s that
                  simple!{' '}
                </p>
              </div>

              <img
                src="/images/graphic/celebrate.svg"
                className="ml-auto h-100 text-sm hidden sm:block"
                alt="thumbnail"
              />
            </div>
          </div>

          <div className="bg-zinc-900 w-full my-24 py-24 text-white mx-auto text-center flex flex-col items-center">
            <h3 className="text-3xl font-white mb-10 uppercase">Who Can Become an Affiliate?</h3>
            <h2 className="w-11/12 md:w-6/12 text-lg font-light mb-2 text-zinc-300 grid gap-2">
              <span>Anyone with an online presence can join our affiliate program. </span>
              <span>
                Whether you are a blogger, influencer, content creator, or have a website dedicated
                to business solutions, you can start earning with v0 Report.
              </span>
            </h2>
          </div>

          <div className="mx-auto rounded-xl max-w-screen-xl px-4 md:px-0">
            <h2 className="w-fit px-4 mx-auto text-center text-3xl font-bold mb-10">
              Getting Started
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mx-auto mb-24">
              <GettingStarted
                Icon={UserSearch}
                title="Apply to the Program"
                description="Click the “Join Now” button to complete the affiliate application form. Our team will review your application."
              />
              <GettingStarted
                Icon={Link2}
                title="Receive Your Unique Link"
                description="Once approved, you’ll receive a unique affiliate link and access to our marketing materials."
              />
              <GettingStarted
                Icon={Volume2}
                title="Start Promoting"
                description="Share your link and start earning commissions on every referral."
              />
            </div>
          </div>

          <section className="bg-white dark:bg-gray-900">
            <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
              <h2 className="mb-8 text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white">
                Frequently asked questions
              </h2>
              <div className="grid pt-8 text-left border-t border-gray-200 md:gap-16 dark:border-gray-700 md:grid-cols-2">
                {faqItems.map((item, index) => (
                  <div key={index} className="mb-10">
                    <h3 className="flex items-center mb-4 text-lg font-medium text-gray-900 dark:text-white">
                      {/* SVG icon */}
                      <svg
                        className="flex-shrink-0 mr-2 w-5 h-5 text-gray-500 dark:text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      {item.question}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {item.answer}
                      {item.linkText && (
                        <a
                          href={item.linkHref}
                          className="font-medium underline text-primary-600 dark:text-primary-500 hover:no-underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.linkText}
                        </a>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div className="px-4 mb-12">
            <div className="rounded-3xl bg-zinc-100 p-16 mx-auto md:max-w-screen-xl gap-0 md:gap-8 px-4 md:px-0">
              <img
                src="/images/graphic/support.svg"
                className="mx-auto w-fit h-16 text-sm mb-6"
                alt="thumbnail"
              />
              <div className="grid gap-2 text-center w-full md:w-2/3 mx-auto">
                <h2 className="w-fit px-4 mx-auto text-left text-xl font-semibold">
                  Still have questions?
                </h2>
                <p className="text-lg text-zinc-500 text-center">
                  Have more questions? Reach out to our affiliate support team at team@v0.report
                  We’re here to help!
                </p>
              </div>
            </div>
          </div>

          <div className="px-2">
            <div className="rounded-3xl bg-zinc-100 p-16 mx-auto max-w-screen-xl flex items-center gap-0 md:gap-8">
              <div className="grid gap-10">
                <h2 className="w-fit px-4 text-left text-3xl font-bold">About v0 Report</h2>
                <p className="text-lg text-zinc-500 grid gap-2">
                  <span>
                    v0 Report is an AI-powered document generator tool that makes creating reports
                    quick and easy.
                  </span>
                  <span>
                    With v0 Report, you can generate professional-quality reports quickly, saving
                    you effort and boosting your productivity. Ideal for business, school, or
                    personal use, our advanced AI technology does the hard work for you.{' '}
                  </span>
                  <span>Experience the future of document creation with v0 Report.</span>
                  <span>Join our affiliate program today and be part of the success story.</span>
                </p>
                <Link
                  className="w-full md:w-fit h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                  href={'/auth/signup'}
                >
                  Sign up now
                </Link>
              </div>

              <img
                src="/images/graphic/logo.jpg"
                className="mx-auto w-7/12 rounded-3xl text-sm hidden sm:block"
                alt="thumbnail"
              />
            </div>
          </div>

          <div className="w-11/12 mx-auto my-48 md:w-fit text-center grid gap-8">
            <Link
              rel="canonical"
              href={isSubscribed ? '/home' : '/'}
              className="flex justify-center w-fit mx-auto"
            >
              <Image
                className="object-scale-down h-8 w-8"
                src="/images/logo.png"
                alt="Logo"
                width={200}
                height={300}
              />
              <p className="my-auto ml-4 hidden sm:block bg-gray-100 text-gray-600 px-3 py-1.5 rounded ">
                v0 report
              </p>
            </Link>
            <h2 className="text-3xl tracking-tight font-semibold leading-tight text-gray-900">
              Experience the future of document generation today <br />
              with v0 Report!
            </h2>
            <Link
              className="w-fit mx-auto h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
              href={'/auth/signup'}
            >
              Get started
            </Link>
          </div>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
